import { darken, lighten } from '@mui/material';

// Base color tokens
export const ORANGE = {
  50: '#FFEDEC',
  100: '#FFD3D0',
  200: '#FBB2AC',
  300: '#F69992',
  400: '#FA746B', // main brand color
  500: '#F55247',
  600: '#E12114',
  700: '#BF180D',
  800: '#86150E',
  900: '#600B06',
};

export const NAVY = {
  50: '#F6F9FB',
  100: '#E0E8F0',
  200: '#C8D3DF',
  300: '#9FAFC3',
  400: '#8598AF',
  500: '#6B819C',
  600: '#516B89',
  700: '#385677',
  800: '#1A4263',
  900: '#111827', // this is the primary navy
};

export const BLUE = {
  50: '#EEF7FC',
  100: '#C9E8F7',
  200: '#A3D8F3',
  300: '#7CC9F0',
  400: '#53BBEF',
  500: '#28ADF0',
  600: '#0C9BE2',
  700: '#0780BD',
  800: '#036697',
  900: '#00293D', // this is the primary blue
};

export const GRAY = {
  50: '#F9FAFB',
  100: '#F3F4F6',
  200: '#E5E7EB',
  300: '#D1D5DB',
  400: '#9CA3AF',
  500: '#6B7280',
  600: '#4B5563',
  700: '#374151',
  800: '#1F2937',
  900: '#111827',
};

export const NEUTRAL_GRAY = {
  50: '#FAFAFA',
  100: '#F5F5F5',
  200: '#E5E5E5',
  300: '#D4D4D4',
  400: '#A3A3A3',
  500: '#737373',
  600: '#525252',
  700: '#404040',
  800: '#262626',
  900: '#171717',
};

export const TEAL = {
  50: '#EFFBF8',
  100: '#D1F6EC',
  200: '#BFEFE2',
  300: '#96E7D2',
  400: '#6FDEC1',
  500: '#40D4AC',
  600: '#22AC88',
  700: '#148366',
  800: '#155B48',
  900: '#08211A',
};

const FUNCTIONAL_SUCCESS = {
  50: TEAL[50],
  100: TEAL[100],
  200: TEAL[200],
  300: TEAL[300],
  400: TEAL[400],
  500: TEAL[500],
  600: TEAL[600],
  700: TEAL[700],
  800: TEAL[800],
  900: TEAL[900],
};
const DEFAULT_FUNCTIONAL_SUCCESS = FUNCTIONAL_SUCCESS[500];

const FUNCTIONAL_WARNING = {
  50: '#FDF5D3',
  100: '#FBE9AE',
  200: '#F8DA89',
  300: '#F5C965',
  400: '#F1B441',
  500: '#ED9D1E',
  600: '#D08013',
  700: '#AB6411',
  800: '#864A0E',
  900: '#2C2607',
};
const DEFAULT_FUNCTIONAL_WARNING = FUNCTIONAL_WARNING[400];

const FUNCTIONAL_ERROR = {
  50: ORANGE[50],
  100: ORANGE[100],
  200: ORANGE[200],
  300: ORANGE[300],
  400: ORANGE[400],
  500: ORANGE[500],
  600: ORANGE[600],
  700: ORANGE[700],
  800: ORANGE[800],
  900: ORANGE[900],
};
const DEFAULT_FUNCTIONAL_ERROR = FUNCTIONAL_ERROR[600];
const CALLOUT_SURFACE = 'rgba(224, 235, 255, .5)';

export const FUNCTIONAL = {
  SUCCESS: {
    ...FUNCTIONAL_SUCCESS,
    DEFAULT: DEFAULT_FUNCTIONAL_SUCCESS,
  },
  WARNING: {
    ...FUNCTIONAL_WARNING,
    DEFAULT: DEFAULT_FUNCTIONAL_WARNING,
  },
  ERROR: {
    ...FUNCTIONAL_ERROR,
    DEFAULT: DEFAULT_FUNCTIONAL_ERROR,
  },
  CALLOUT: CALLOUT_SURFACE,
  HOVER: GRAY[100],
};

export const BRAND = {
  ORANGE: '#FF594E',
  NAVY: '#001C33',
};

// Semantic text colors. PRIMARY is the dark color used for headings and body
// copy across the app; reference this instead of a raw palette value so the
// "primary text" color can be changed in one place. SECONDARY is the muted
// color for supporting copy, labels, captions, and the icons that pair with
// them.
const SECONDARY_TEXT = GRAY[500];
export const TEXT = {
  PRIMARY: GRAY[800],
  SECONDARY: SECONDARY_TEXT,
  // Derived from SECONDARY so these state colors track it automatically.
  // ~20% darker than SECONDARY, to preserve a visible hover affordance where
  // the resting text/icon color is already SECONDARY.
  SECONDARY_HOVER: darken(SECONDARY_TEXT, 0.2),
  // ~20% lighter than SECONDARY, for placeholder/hint text so it reads as an
  // empty field rather than as entered secondary copy.
  PLACEHOLDER: lighten(SECONDARY_TEXT, 0.2),
  // ~40% lighter than SECONDARY (same derivation approach as PLACEHOLDER, but
  // lighter) so disabled copy reads as inactive rather than as active
  // supporting text.
  DISABLED: lighten(SECONDARY_TEXT, 0.4),
};

export const PRIMITIVES = {
  WHITE: '#FFFFFF',
  BLACK: '#111827',
};

const CATEGORIES = {
  IN_ESTATE: NAVY,
  FAMILY_GIVING: TEAL,
  CHARITABLE_GIVING: BLUE,
  ESTATE_TAX_SAVINGS: ORANGE,
};

const MATH = {
  NEGATIVE: ORANGE[600],
};

export const COLORS = {
  GRAY,
  NEUTRAL_GRAY,

  TEAL,
  BLUE,
  NAVY,

  ORANGE,

  BRAND,

  TEXT,

  FUNCTIONAL,
  PRIMITIVES,
  CATEGORIES,
  MATH,
};

/**
 * Color of the blue "halo" outline drawn around an element on hover. Exposed
 * on its own (in addition to `HOVER_HALO_OUTLINE`) for hover treatments that
 * compose their own outline — e.g. the movable cards animate the color to
 * transparent while a freshly-duplicated card fades out.
 */
export const HOVER_HALO_COLOR = COLORS.BLUE[600];

/**
 * The hover "halo" outline as one shared set of properties. String values so
 * it works as both an MUI `sx` and a plain `React.CSSProperties`. Spread it for
 * the standard treatment; treatments that need to vary a piece (e.g. a larger
 * offset, or a fading color) can spread it and override that property.
 */
export const HOVER_HALO_OUTLINE = {
  outline: `1px solid ${HOVER_HALO_COLOR}`,
  // Inset by 1px so the halo hugs the element's edge in dense layouts.
  outlineOffset: '-1px',
  borderRadius: '4px',
} as const;
